// Enhanced formatters.js with Greek to Latin transliteration

/**
 * Transliterate Greek characters to Latin equivalents
 * @param {string} text - Text with possible Greek characters
 * @returns {string} - Text with Greek characters converted to Latin equivalents
 */
export const transliterateGreekToLatin = (text) => {
  if (!text) return '';
  
  // Greek to Latin character mapping
  const greekToLatinMap = {
    // Lowercase letters
    'α': 'a', 'ά': 'a',
    'β': 'v',
    'γ': 'g',
    'δ': 'd',
    'ε': 'e', 'έ': 'e',
    'ζ': 'z',
    'η': 'i', 'ή': 'i',
    'θ': 'th',
    'ι': 'i', 'ί': 'i', 'ϊ': 'i', 'ΐ': 'i',
    'κ': 'k',
    'λ': 'l',
    'μ': 'm',
    'ν': 'n',
    'ξ': 'x',
    'ο': 'o', 'ό': 'o',
    'π': 'p',
    'ρ': 'r',
    'σ': 's', 'ς': 's',
    'τ': 't',
    'υ': 'y', 'ύ': 'y', 'ϋ': 'y', 'ΰ': 'y',
    'φ': 'f',
    'χ': 'ch',
    'ψ': 'ps',
    'ω': 'o', 'ώ': 'o',
    
    // Uppercase letters
    'Α': 'A', 'Ά': 'A',
    'Β': 'V',
    'Γ': 'G',
    'Δ': 'D',
    'Ε': 'E', 'Έ': 'E',
    'Ζ': 'Z',
    'Η': 'I', 'Ή': 'I',
    'Θ': 'Th',
    'Ι': 'I', 'Ί': 'I', 'Ϊ': 'I',
    'Κ': 'K',
    'Λ': 'L',
    'Μ': 'M',
    'Ν': 'N',
    'Ξ': 'X',
    'Ο': 'O', 'Ό': 'O',
    'Π': 'P',
    'Ρ': 'R',
    'Σ': 'S',
    'Τ': 'T',
    'Υ': 'Y', 'Ύ': 'Y', 'Ϋ': 'Y',
    'Φ': 'F',
    'Χ': 'Ch',
    'Ψ': 'Ps',
    'Ω': 'O', 'Ώ': 'O'
  };
  
  // Replace each Greek character with its Latin equivalent
  return text.split('').map(char => {
    return greekToLatinMap[char] || char;
  }).join('');
};

/**
 * Convert an NPC name to a tag format used in commands
 * First transliterates any Greek characters to Latin equivalents,
 * then converts to lowercase, replaces spaces with underscores,
 * and removes non-alphanumeric characters
 * 
 * @param {string} name - The NPC name to convert
 * @returns {string} The lowercase tag version of the name
 */
export const nameToTag = (name) => {
  if (!name) {
    console.log('nameToTag received empty name');
    return '';
  }
  
  // First transliterate any Greek characters to Latin equivalents
  const transliterated = transliterateGreekToLatin(name);
  
  // Then perform the standard tag conversion
const tag = transliterated.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '');  
  console.log(`nameToTag conversion: "${name}" -> "${tag}"`);
  return tag;
};

/**
 * Truncate text to a certain length and add ellipsis if needed
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 30) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

/**
 * Generate a dialogue key for .lang file
 * @param {string} npcTag - The NPC tag
 * @param {number} index - The dialogue index
 * @returns {string} Formatted dialogue key
 */
export const dialogueKey = (npcTag, index) => {
  return `dialogue.${npcTag}.${index}`;
};

/**
 * Generate a button key for .lang file
 * @param {string} button - The button text
 * @returns {string} Formatted button key
 */
export const buttonKey = (button) => {
  // Use nameToTag which now includes transliteration
  const buttonLowercase = nameToTag(button);
  return `dialogue.button.${buttonLowercase}`;
};

/**
 * Generate a command to open a dialogue
 * @param {string} npcTag - The NPC tag
 * @param {number} dialogueIndex - The dialogue index
 * @returns {string} Formatted command
 */
export const generateDialogueOpenCommand = (npcTag, dialogueIndex) => {
  return `/dialogue open @e[tag=${npcTag}] @s ${npcTag}.${dialogueIndex}`;
};

/**
 * Generate a command to set a scoreboard value
 * @param {string} npcTag - The NPC tag
 * @param {number} dialogueIndex - The dialogue index
 * @returns {string} Formatted command
 */
export const generateScoreboardCommand = (npcTag, dialogueIndex) => {
  return `/scoreboard players set @initiator ${npcTag} ${dialogueIndex}`;
};